import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import TaskChecklist from './TaskChecklist';
import { colorOptions } from '../../utils/colorUtils';

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    if (isNaN(date.getTime())) return '';
    return format(date, 'MMM d, yyyy h:mm a');
  } catch (error) {
    console.error('Date formatting error:', error);
    return '';
  }
};

function TaskDialog({ open, onClose, task, onSave }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [checklist, setChecklist] = useState([]);

  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDescription(task.description || '');
      setDueDate(task.dueDate ? parseISO(task.dueDate) : null);
      setBackgroundColor(task.backgroundColor || '#ffffff');
      setChecklist(task.checklist || []);
    } else {
      setTitle('');
      setDescription('');
      setDueDate(null);
      setBackgroundColor('#ffffff');
      setChecklist([]);
    }
  }, [task]);

  const handleSave = () => {
    console.log('Saving task with dueDate:', dueDate);
    const taskData = {
      title,
      description,
      dueDate: dueDate ? dueDate.toISOString() : null,
      backgroundColor,
      checklist: checklist.map(item => ({
        text: item.text,
        description: item.description || '',
        completed: item.completed || false,
        _id: item._id?.startsWith('temp-') ? undefined : item._id,
        ...(item.createdBy && { createdBy: item.createdBy }),
        ...(item.createdAt && { createdAt: item.createdAt }),
        ...(item.lastModifiedBy && { lastModifiedBy: item.lastModifiedBy }),
        ...(item.lastModifiedAt && { lastModifiedAt: item.lastModifiedAt }),
        ...(item.completedBy && { completedBy: item.completedBy }),
        ...(item.completedAt && { completedAt: item.completedAt })
      })),
    };
    onSave(taskData);
    onClose();
  };

  const handleAddChecklistItem = () => {
    if (newChecklistItem.trim()) {
      setChecklist([...checklist, { 
        _id: `temp-${Date.now()}`,  // Temporary ID for new items
        text: newChecklistItem.trim(),
        completed: false
      }]);
      setNewChecklistItem('');
    }
  };

  const handleDeleteChecklistItem = (index) => {
    const newChecklist = [...checklist];
    newChecklist.splice(index, 1);
    setChecklist(newChecklist);
  };

  const handleChecklistReorder = (newItems) => {
    setChecklist(newItems);
  };

  const handleChecklistToggle = (itemId) => {
    setChecklist(items => items.map(item =>
      item._id === itemId 
        ? { ...item, completed: !item.completed }
        : item
    ));
  };

  const handleChecklistUpdate = (itemId, updates) => {
    setChecklist(items => items.map(item =>
      item._id === itemId 
        ? { ...item, ...updates }
        : item
    ));
  };

  const handleChecklistDelete = (itemId) => {
    setChecklist(items => items.filter(item => item._id !== itemId));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {task ? 'Edit Task' : 'Create Task'}
        {task && task.createdBy && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Created by {task.createdBy.firstName} {task.createdBy.lastName}
            {task.createdAt && ` on ${formatDate(task.createdAt)}`}
            {task.lastModifiedBy && (
              <>
                <br />
                Last modified by {task.lastModifiedBy.firstName} {task.lastModifiedBy.lastName}
                {task.lastModifiedAt && ` on ${formatDate(task.lastModifiedAt)}`}
              </>
            )}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
        />

        <FormControl fullWidth margin="normal">
          <InputLabel id="color-select-label">Background Color</InputLabel>
          <Select
            labelId="color-select-label"
            value={backgroundColor}
            onChange={(e) => setBackgroundColor(e.target.value)}
            label="Background Color"
          >
            {colorOptions.map(color => (
              <MenuItem 
                key={color.value} 
                value={color.value}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: 1,
                    backgroundColor: color.value,
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}
                />
                {color.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          margin="normal"
          multiline
          rows={3}
        />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Due Date"
            value={dueDate}
            onChange={(newValue) => setDueDate(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: "normal"
              }
            }}
          />
        </LocalizationProvider>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Checklist
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="New item"
              value={newChecklistItem}
              onChange={(e) => setNewChecklistItem(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddChecklistItem();
                }
              }}
            />
            <IconButton onClick={handleAddChecklistItem} color="primary">
              <AddIcon />
            </IconButton>
          </Box>

          <TaskChecklist
            items={checklist}
            onReorder={handleChecklistReorder}
            onToggle={handleChecklistToggle}
            onUpdate={handleChecklistUpdate}
            onDelete={handleChecklistDelete}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!title.trim()}>
          {task ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TaskDialog; 